/*
 * Copyright (c) 2026 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();import { type BeforeSendEvent, track } from '@vercel/analytics'

import type { ContactMethod } from '@/lib/analytics/contact-links'

/** Query parameters that identify a single ad click. They are removed from every URL sent to Vercel. */
const CLICK_ID_PARAMETERS = ['gclid', 'gbraid', 'wbraid']

/** Where a visitor made a cookie choice. */
type CookieChoiceSource = 'banner' | 'preferences'

/**
 * Every custom event sent to Vercel Web Analytics, mapped to its properties (`undefined` for none). Vercel Analytics
 * is cookieless and records the page path with each event, so events are counted for every visitor and the region can
 * be read from the path. Properties are categories and amounts only: never put personal data or identifiers in them.
 */
type VercelEventMap = {
  /** The cookie banner was shown to a visitor who has not made a choice yet. */
  cookie_banner_shown: undefined

  /** The cookie preferences dialog was opened. */
  cookie_preferences_opened: { source: 'banner' | 'floating_button' }

  /** A visitor accepted or rejected analytical cookies. */
  cookie_consent: { choice: 'accept' | 'reject'; source: CookieChoiceSource }

  /** The personalised suggestion enquiry was sent successfully. */
  enquiry_submitted: { userType: string }

  /** An embedded Zoho form was rendered on a page. It may not have been scrolled into view. */
  zoho_form_rendered: { form: string }

  /** A visitor arrived from a Google Ads click. */
  landed_with_gclid: undefined

  /** A visitor clicked an email, phone, WhatsApp or booking link. They may not have gone on to make contact. */
  contact_click: { method: ContactMethod }

  /** A visitor changed region with the region selector. */
  region_switched: { from: string; to: string }

  /** A visitor started checkout. */
  begin_checkout: { currency: string; value: number }

  /** A visitor completed a purchase. */
  purchase: { currency: string; value: number }
}

/** The name of a custom Vercel Analytics event. */
type VercelEventName = keyof VercelEventMap

/** The arguments for an event: just its name when it has no properties, otherwise its name and properties. */
type VercelEventArguments<T extends VercelEventName> = VercelEventMap[T] extends undefined
  ? [event: T]
  : [event: T, properties: VercelEventMap[T]]

/**
 * Sends a strictly typed custom event to Vercel Web Analytics. In development, Vercel logs the event to the console
 * instead of sending it.
 *
 * @param args - the event name, followed by its properties when the event has any
 */
const trackVercelEvent = <T extends VercelEventName>(...args: VercelEventArguments<T>): void => {
  const [event, properties] = args

  track(event, properties)
}

/**
 * Removes ad click IDs from the URL of a pageview or event before Vercel sends it. Vercel sends the full page URL with
 * every pageview and event, so without this a GCLID in the landing URL would reach Vercel. UTM parameters are kept.
 *
 * @param event - the pageview or event about to be sent
 */
const withoutClickIds = (event: BeforeSendEvent): BeforeSendEvent => {
  const url = new URL(event.url)

  for (const parameter of CLICK_ID_PARAMETERS) {
    url.searchParams.delete(parameter)
  }

  return { ...event, url: url.toString() }
}

export { trackVercelEvent, withoutClickIds, type VercelEventMap, type VercelEventName }
